import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';
import ActionTypes from '../../../constants/ActionTypes';
import MiddlewareEntities from "../../../constants/MiddlewareEntities";
import { setCaptchaRequiredActions } from "../captcha/actions";
import { requestFetchManhattanOrderAction } from "../order/manhattan/transition/actions";
import { setUserLabelMessage } from "../labelUserMessage/actions";
import { setMaintenanceMode, setNotFound } from "../redirection/actions";
import { apiError, apiSuccess } from './actions';
import HttpRequest from '../../../services/HttpRequest';
import SessionDataFactory from "../../../factories/SessionDataFactory";
import { setSessionData } from "../sessionData/actions";
import LoginDataFactory from "../../../factories/LoginDataFactory";
import { setLoginDataAction } from "../pageShell/actions";
import { setShouldCallLogin } from "../cookie/actions";

const entitiesForMaintanceMode = [
  MiddlewareEntities.PAGE_SHELL,
  MiddlewareEntities.PRODUCTS,
  MiddlewareEntities.PRODUCT,
  MiddlewareEntities.MANHATTAN.BUNDLE ||
  MiddlewareEntities.MANHATTAN.FTTH.ADDITIONAL_DEVICES ||
  MiddlewareEntities.MANHATTAN.FTTH.FTTH_PRODUCT ||
  MiddlewareEntities.MANHATTAN.FTTH.SINGLE_PRODUCT ||
  MiddlewareEntities.MANHATTAN.POST_OTT.SINGLE_PRODUCT ||
  MiddlewareEntities.MANHATTAN.OTT.OTT_PRODUCT
];

const apiMiddleware: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => async (action: AnyAction) => {
  next(action);

  if (action.type === ActionTypes.API_REQUEST) {
    const { entity } = action.payload.meta;
    localStorage.setItem("latestRequest", JSON.stringify(action.payload.meta));
    try {
      const lang = store.getState().language.language;
      action.payload.meta.headers = { ...action.payload.meta.headers, lang };
      const response = await HttpRequest.request(action.payload.meta);
      store.dispatch(apiSuccess(response.data, entity, action.payload.meta.params, action.payload.meta.data));
      if (response.data.session) {
        const sessionDataObject = SessionDataFactory.create(response.data.session);
        store.dispatch(setSessionData(sessionDataObject, action.payload?.source));

        if (sessionDataObject.redirectUser) {
          store.dispatch(setShouldCallLogin(true));
          return;
        }

        const language = store.getState().language.language;
        const section = store.getState().section.section;
        const numberOfOrderItems = store.getState().session.numberOfOrderItems;
        const user = store.getState().session.user;
        const labels = require(`../../../translations/${language}`);
        const loginData = LoginDataFactory.create(section, user, labels, numberOfOrderItems);

        store.dispatch(setLoginDataAction(loginData));
      }
    } catch (error) {
      return dispatchActionOnError(entity, error, store);
    }
  }
};

export const checkToDispatchMaintenanceMode = (entity: string, status: number): boolean => {
  return entitiesForMaintanceMode.includes(entity) && status === 503;
};

export const dispatchActionOnError = (entity: string, error: any, store: MiddlewareAPI) => {
  if (checkToDispatchMaintenanceMode(entity, error.status)) {
    return store.dispatch(setMaintenanceMode());
  }

  if (entity === MiddlewareEntities.AVAILABILITY_PRODUCT && error.status === 429) {
    return store.dispatch(setCaptchaRequiredActions(true, entity));
  }

  if (entity === MiddlewareEntities.PRODUCT  && error.status === 404) {
    return store.dispatch(setNotFound());
  }

  if (entity === MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP && error.status === 409) {
    return store.dispatch(requestFetchManhattanOrderAction({}));
  }

  if (entity === MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP && error.status === 410) {
    return store.dispatch(setUserLabelMessage("chosenAppointmentIsNotAvailable", entity));
  }

  return store.dispatch(apiError(error, entity));
};

export default apiMiddleware;
